import React, { useState } from "react"
import styled from "styled-components"
import { State, useHookstate } from "@hookstate/core"
import { Validation } from "@hookstate/validation"
import { useLocation } from "react-router"
import { LinkContainer } from "react-router-bootstrap"
import { Nav } from "react-bootstrap"
import { useLanguageState } from "../../globalStates/LanguageState"
import { useExhibitorDataState } from "../../globalStates/ExhibitorDataState"
import { useConfigDataState } from "../../globalStates/ConfigDataState"
import { hasServiceKeys } from "../../globalStates/ConfigDataState"
import WarningIcon from "../../images/warning.svg"
import Languages from "./Languages"
import branding, { SidebarSection } from "../../branding/branding"
import {
    categoriesPageRoute,
    couponsPageRoute,
    eventDatesPageRoute,
    exhibitorNewsPageRoute,
    homePageRoute,
    jobsPageRoute,
    licencesPageRoute,
    linksPageRoute,
    notificationTemplatesPageRoute,
    multimediaPageRoute,
    organizerNewsPageRoute,
    printPageRoute,
    productsPageRoute,
    profilePageRoute,
    showroomsPageRoute,
    storyNewsPageRoute,
    teamPageRoute,
    trademarksPageRoute,
    productsForCollectionsPageRoute
} from "../../utils/RoutePaths"
import Button from "../../components/Button"
import EventShopIcon from "../../images/eventshop.svg"
import EventGuideIcon from "../../images/eventguide.svg"

interface SidebarWrapperProps {
    color: string
}

const SidebarWrapper = styled.nav<SidebarWrapperProps>`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;
    z-index: 2;
    width: 270px;
    float: left;
    height: 100vh;
    overflow: auto;
    background: #fff;
    box-shadow: 0px -10px 4px rgba(0, 0, 0, 0.25);

    button,
    button:focus,
    button:active:focus,
    button.inverse:not(:disabled):not(.disabled):active:focus {
        color: ${branding.configuration.maincolor};
        background-color: #fff;
        border: 1px solid ${branding.configuration.maincolor};
        border-radius: 5px;
        width: 100px;
        margin: 0 10px;
        font-size: 14px;
        line-height: 18px;
        text-align: center;
    }
    button:focus,
    button:not(:disabled):not(.disabled):active:focus {
        box-shadow: none;
    }
    button:not(:disabled):not(.disabled):active:focus {
        color: #fff;
        background-color: ${branding.configuration.maincolor};
        border: 1px solid ${branding.configuration.maincolor};
    }
    button:hover {
        color: ${branding.configuration.maincolor};
        background-color: #fff;
        border: 1px solid ${branding.configuration.maincolor};
    }
    button.inverse {
        color: #fff;
        background-color: ${branding.configuration.maincolor};
        border: 1px solid ${branding.configuration.maincolor};
    }
    button.inverse:hover {
        color: #fff;
        background-color: ${branding.configuration.maincolor};
        border: 1px solid ${branding.configuration.maincolor};
    }

    button.btn-link {
        border: none;
    }

    & > .top > .nav {
        padding: 20px 10px 10px 10px;
        .nav-item {
            display: flex;

            img {
                height: 25px;
                width: 25px;
                margin-top: 10px;
            }
        }
        .nav-link {
            padding: 12px 15px;
            color: #727272;
            font-weight: 500;
        }
        .nav-link.active {
            color: ${(props) => props.color || "#6DD400"};
        }
    }
`

const TopWrapper = styled.div``

const BottomWrapper = styled.div`
    padding: 25px;
`

const Logo = styled.div`
    height: 80px;
    padding: 10px 25px;
    display: flex;
    align-items: center;
`

interface LogoIconProps {
    source?: string
}

const LogoIcon = styled.div<LogoIconProps>`
    height: 60px;
    width: 150px;
    display: flex;
    align-items: center;
    background-image: url(${(props) => (!props.source ? "none" : props.source)});
    background-repeat: no-repeat;
    background-position-y: center;
    background-size: contain;
`

const Buttons = styled.div`
    padding: 40px 0 0 0;
`

const ButtonWrapper = styled.div`
    padding: 0 15px 15px 0;

    & button {
        width: 180px;
        min-width: 180px;
        font-size: 14px;
        line-height: 18px;
        display: flex;
        flex-direction: row;
        text-align: center;
    }
`

interface SidebarItemProps {
    id?: string
    label: string
    path: string
    itemState: State<any, Validation>
    helperItemState: State<any, Validation> //mandatory only temporarily until I find a better way to handle this
    useHelper: boolean
    depth?: number
    search: any
    style?: React.CSSProperties | ((isActive: boolean) => React.CSSProperties) | undefined
}

const SidebarItem: React.FC<SidebarItemProps> = ({
    id,
    label,
    path,
    itemState,
    helperItemState,
    useHelper,
    depth,
    search,
    style
}) => {
    const scopedItemState = useHookstate(itemState)
    const errors = scopedItemState.errors(undefined, depth)

    const scopedHelperItemState = useHookstate(helperItemState)
    const helperErrors = scopedHelperItemState.errors(undefined, depth)

    const errorsNumber = errors.length + (useHelper ? helperErrors.length : 0)

    return (
        <Nav.Item id={id}>
            <LinkContainer style={style} to={{ pathname: path, search }}>
                <Nav.Link href={path}>{label}</Nav.Link>
            </LinkContainer>
            {errorsNumber !== 0 && <img src={WarningIcon} alt="warning" />}
        </Nav.Item>
    )
}

interface SidebarItemLinkProps {
    label: string
    path: string
}

const SidebarItemLink: React.FC<SidebarItemLinkProps> = ({ label, path }) => (
    <Nav.Item>
        <Nav.Link href={path} target="_blank">
            {label}
        </Nav.Link>
    </Nav.Item>
)

const Sidebar: React.FC<{}> = () => {
    const location = useLocation()
    const branding = useLanguageState().getStrings()
    const [activePage, setActivePage] = useState(location.pathname)

    const sectionsOrder = branding.configuration.sidebar.sectionsOrder
    const exiDataState = useExhibitorDataState()
    const organization = exiDataState.getOrganization()
    const serviceKeys = useConfigDataState().getServiceKeys()
    const categoryVisible = hasServiceKeys(serviceKeys.category)
    const linkVisible = hasServiceKeys(serviceKeys.link)
    const productsVisible = hasServiceKeys(serviceKeys.products)
    const productsForCollectionsVisible = hasServiceKeys(serviceKeys.productsForCollections)
    const trademarksVisible = hasServiceKeys(serviceKeys.trademarks)
    const teamVisible = hasServiceKeys(serviceKeys.contactPartner)
    const multimediaVisible = hasServiceKeys(serviceKeys.multimedia)
    const exhibitorNewsVisible = hasServiceKeys(serviceKeys.news)
    const storyNewsVisible = hasServiceKeys(serviceKeys.stories)
    const showroomsVisible =
        (serviceKeys.showroomPublic !== undefined && hasServiceKeys(serviceKeys.showroomPublic)) ||
        (serviceKeys.showroomPrivate !== undefined && hasServiceKeys(serviceKeys.showroomPrivate))
    const couponsVisible = hasServiceKeys(serviceKeys.goodies)
    const jobsVisible = hasServiceKeys(serviceKeys.jobAd)
    const printVisible =
        hasServiceKeys(serviceKeys.printOrganizationName) ||
        hasServiceKeys(serviceKeys.printAddress) ||
        hasServiceKeys(serviceKeys.printAddressCategoryDirectory) ||
        hasServiceKeys(serviceKeys.printContact) ||
        hasServiceKeys(serviceKeys.printContactCategoryDirectory) ||
        hasServiceKeys(serviceKeys.printLogo) ||
        hasServiceKeys(serviceKeys.printLogoExhibitorDirectory) ||
        hasServiceKeys(serviceKeys.printLogoCategoryDirectory) ||
        hasServiceKeys(serviceKeys.printDescription) ||
        hasServiceKeys(serviceKeys.printAd)

    const notificationTemplatesSmallLimit = serviceKeys["notificationTemplate#small"] || 0

    const notificationTemplatesMediumLimit = serviceKeys["notificationTemplate#medium"] || 0

    const notificationTemplatesLargeLimit = serviceKeys["notificationTemplate#large"] || 0

    const notificationTemplatesVisible =
        notificationTemplatesSmallLimit > 0 || notificationTemplatesMediumLimit > 0 || notificationTemplatesLargeLimit > 0

    const eventDatesVisible =
        exiDataState.getEventDates() &&
        exiDataState.getEventDates().length > 0 &&
        (hasServiceKeys(serviceKeys.eventDate) ||
            hasServiceKeys(serviceKeys.eventDateMasterClass) ||
            hasServiceKeys(serviceKeys.eventDateRemoteLiveStream) ||
            hasServiceKeys(serviceKeys.eventDatePreRecorded) ||
            hasServiceKeys(serviceKeys.eventDateRoundTable))

    const getItemColor = (activePage: string) => {
        const path = activePage.substring(0, 8)
        const defaultColor = { color: "#727272" }
        const activePageColor = { color: branding.configuration.maincolor }
        return path === homePageRoute || path === profilePageRoute ? activePageColor : defaultColor
    }

    const visitEventShop = () => {
        window.open(branding.configuration.sidebar.urlEventShop, "_blank")
    }

    const visitEventGuide = () => {
        let organizationName = organization.name.get().replace(/[^a-z0-9]+/gi, "-")
        organizationName = organizationName.replace(/^[^a-z0-9]|[^a-z0-9]$/gi, "")
        window.open(
            `${branding.configuration.sidebar.urlEventGuide}/company/${organizationName}--${organization.id.get()}`,
            "_blank"
        )
    }

    return (
        <SidebarWrapper color={branding.configuration.maincolor}>
            <TopWrapper className="top">
                <Logo>
                    <LogoIcon source="/branding/applicationMedia/logo.png" />
                </Logo>
                <Nav activeKey={activePage} className="flex-column" onSelect={(selectedKey: any) => setActivePage(selectedKey)}>
                    {sectionsOrder.map((section: SidebarSection, index: number) => {
                        switch (section) {
                            case SidebarSection.PROFILE: {
                                return (
                                    <SidebarItem
                                        id={section}
                                        key={section}
                                        style={getItemColor(activePage)}
                                        label={branding.configuration.sidebar.lblProfile}
                                        path={profilePageRoute}
                                        search={location.search}
                                        itemState={exiDataState.getOrganization()}
                                        helperItemState={exiDataState.getOrganization()}
                                        useHelper={false}
                                        depth={2}
                                    />
                                )
                            }

                            case SidebarSection.CATEGORY: {
                                if (categoryVisible) {
                                    return (
                                        <SidebarItem
                                            id={section}
                                            key={section}
                                            label={branding.configuration.sidebar.lblCategories}
                                            path={categoriesPageRoute}
                                            search={location.search}
                                            itemState={exiDataState.getCategories()}
                                            helperItemState={exiDataState.getCategories()}
                                            useHelper={false}
                                        />
                                    )
                                }

                                return null
                            }

                            case SidebarSection.LICENCES: {
                                if (categoryVisible) {
                                    return (
                                        <SidebarItem
                                            id={section}
                                            key={section}
                                            label={branding.configuration.sidebar.lblLicences}
                                            path={licencesPageRoute}
                                            search={location.search}
                                            itemState={exiDataState.getCategories()}
                                            helperItemState={exiDataState.getCategories()}
                                            useHelper={false}
                                        />
                                    )
                                }

                                return null
                            }

                            case SidebarSection.LINKS: {
                                if (linkVisible) {
                                    return (
                                        <SidebarItem
                                            id={section}
                                            key={section}
                                            label={branding.configuration.sidebar.lblLinks}
                                            path={linksPageRoute}
                                            search={location.search}
                                            itemState={exiDataState.getLinks()}
                                            helperItemState={exiDataState.getLinks()}
                                            useHelper={false}
                                        />
                                    )
                                }

                                return null
                            }

                            case SidebarSection.PRODUCTS: {
                                if (productsVisible) {
                                    return (
                                        <SidebarItem
                                            id={section}
                                            key={section}
                                            label={branding.configuration.sidebar.lblProducts}
                                            path={productsPageRoute}
                                            search={location.search}
                                            itemState={exiDataState.getProducts()}
                                            helperItemState={exiDataState.getProducts()}
                                            useHelper={false}
                                        />
                                    )
                                }

                                return null
                            }

                            case SidebarSection.PRODUCTS_FOR_COLLECTIONS: {
                                if (productsForCollectionsVisible) {
                                    return (
                                        <SidebarItem
                                            id={section}
                                            key={section}
                                            label={branding.configuration.sidebar.lblProductsForCollections}
                                            path={productsForCollectionsPageRoute}
                                            search={location.search}
                                            itemState={exiDataState.getProductsForCollections()}
                                            helperItemState={exiDataState.getProductsForCollections()}
                                            useHelper={false}
                                        />
                                    )
                                }

                                return null
                            }

                            case SidebarSection.TRADEMARKS: {
                                if (trademarksVisible) {
                                    return (
                                        <SidebarItem
                                            id={section}
                                            key={section}
                                            label={branding.configuration.sidebar.lblTrademarks}
                                            path={trademarksPageRoute}
                                            search={location.search}
                                            itemState={exiDataState.getTrademarks()}
                                            helperItemState={exiDataState.getTrademarks()}
                                            useHelper={false}
                                        />
                                    )
                                }

                                return null
                            }

                            case SidebarSection.TEAM: {
                                if (teamVisible) {
                                    return (
                                        <SidebarItem
                                            id={section}
                                            key={section}
                                            label={branding.configuration.sidebar.lblTeam}
                                            path={teamPageRoute}
                                            search={location.search}
                                            itemState={exiDataState.getPersons()}
                                            helperItemState={exiDataState.getPersons()}
                                            useHelper={false}
                                        />
                                    )
                                }
                                return null
                            }

                            case SidebarSection.MULTIMEDIA: {
                                if (multimediaVisible) {
                                    return (
                                        <SidebarItem
                                            id={section}
                                            key={section}
                                            label={branding.configuration.sidebar.lblMultimedia}
                                            path={multimediaPageRoute}
                                            search={location.search}
                                            itemState={exiDataState.getMedias()}
                                            helperItemState={exiDataState.getMedias()}
                                            useHelper={false}
                                        />
                                    )
                                }

                                return null
                            }

                            case SidebarSection.NEWS:
                            case SidebarSection.EXHIBITOR_NEWS: {
                                if (exhibitorNewsVisible) {
                                    return (
                                        <SidebarItem
                                            id={section}
                                            key={section}
                                            label={branding.configuration.sidebar.lblExhibitorNews}
                                            path={exhibitorNewsPageRoute}
                                            search={location.search}
                                            itemState={exiDataState.getExhibitorNews()}
                                            helperItemState={exiDataState.getExhibitorNews()}
                                            useHelper={false}
                                        />
                                    )
                                }

                                return null
                            }

                            case SidebarSection.ORGANIZER_NEWS: {
                                if (exiDataState.getOrganizerNews().length > 0) {
                                    return (
                                        <SidebarItem
                                            id={section}
                                            key={section}
                                            label={branding.configuration.sidebar.lblOrganizerNews}
                                            path={organizerNewsPageRoute}
                                            search={location.search}
                                            itemState={exiDataState.getOrganizerNews()}
                                            helperItemState={exiDataState.getOrganizerNews()}
                                            useHelper={false}
                                        />
                                    )
                                }

                                return null
                            }

                            case SidebarSection.STORY_NEWS: {
                                if (storyNewsVisible) {
                                    return (
                                        <SidebarItem
                                            id={section}
                                            key={section}
                                            label={branding.configuration.sidebar.lblStoryNews}
                                            path={storyNewsPageRoute}
                                            search={location.search}
                                            itemState={exiDataState.getStoryNews()}
                                            helperItemState={exiDataState.getStoryNews()}
                                            useHelper={false}
                                        />
                                    )
                                }

                                return null
                            }

                            case SidebarSection.EVENTDATES: {
                                if (eventDatesVisible) {
                                    return (
                                        <SidebarItem
                                            id={section}
                                            key={section}
                                            label={branding.configuration.sidebar.lblEventDate}
                                            path={eventDatesPageRoute}
                                            search={location.search}
                                            itemState={exiDataState.getEventDates()}
                                            helperItemState={exiDataState.getEventDates()}
                                            useHelper={false}
                                        />
                                    )
                                }

                                return null
                            }

                            case SidebarSection.SHOWROOMS: {
                                if (showroomsVisible) {
                                    return (
                                        <SidebarItem
                                            id={section}
                                            key={section}
                                            label={branding.configuration.sidebar.lblShowrooms}
                                            path={showroomsPageRoute}
                                            search={location.search}
                                            itemState={exiDataState.getShowrooms()}
                                            helperItemState={exiDataState.getShowrooms()}
                                            useHelper={false}
                                        />
                                    )
                                }

                                return null
                            }

                            case SidebarSection.COUPONS: {
                                if (couponsVisible) {
                                    return (
                                        <SidebarItem
                                            id={section}
                                            key={section}
                                            label={branding.configuration.sidebar.lblCoupons}
                                            path={couponsPageRoute}
                                            search={location.search}
                                            itemState={exiDataState.getCoupons()}
                                            helperItemState={exiDataState.getCoupons()}
                                            useHelper={false}
                                        />
                                    )
                                }

                                return null
                            }

                            case SidebarSection.JOBS: {
                                if (jobsVisible) {
                                    return (
                                        <SidebarItem
                                            id={section}
                                            key={section}
                                            label={branding.configuration.sidebar.lblJobs}
                                            path={jobsPageRoute}
                                            search={location.search}
                                            itemState={exiDataState.getJobs()}
                                            helperItemState={exiDataState.getJobs()}
                                            useHelper={false}
                                        />
                                    )
                                }

                                return null
                            }

                            case SidebarSection.PRINT: {
                                if (printVisible) {
                                    return (
                                        <SidebarItem
                                            id={section}
                                            key={section}
                                            label={branding.configuration.sidebar.lblPrint}
                                            path={printPageRoute}
                                            search={location.search}
                                            itemState={exiDataState.getPrintCatalog()}
                                            helperItemState={exiDataState.getPrintCatalog()}
                                            useHelper={false}
                                        />
                                    )
                                }

                                return null
                            }

                            case SidebarSection.NOTIFICATION_TEMPLATES: {
                                if (notificationTemplatesVisible) {
                                    return (
                                        <SidebarItem
                                            id={section}
                                            key={section}
                                            label={branding.configuration.sidebar.lblNotificationTemplates}
                                            path={notificationTemplatesPageRoute}
                                            search={location.search}
                                            itemState={exiDataState.getNotificationTemplates()}
                                            helperItemState={exiDataState.getNotificationPromotions()}
                                            useHelper={true}
                                        />
                                    )
                                }

                                return null
                            }

                            case SidebarSection.CUSTOM_LINK: {
                                return (
                                    <React.Fragment key={section}>
                                        <br />
                                        <br />
                                        <br />
                                        <br />
                                        <SidebarItemLink
                                            label={branding.configuration.sidebar.lblCustomLink}
                                            path={branding.configuration.sidebar.urlCustomLink}
                                        />
                                    </React.Fragment>
                                )
                            }

                            default:
                                return null
                        }
                    })}

                    <Buttons>
                        {branding.configuration.sidebar.showEventShop && (
                            <React.Fragment key={"EVENT_SHOP"}>
                                <ButtonWrapper>
                                    <Button
                                        id="shop"
                                        caption={branding.configuration.sidebar.lblEventShop}
                                        icon={EventShopIcon}
                                        inverse
                                        onClick={visitEventShop}
                                    />
                                </ButtonWrapper>
                            </React.Fragment>
                        )}

                        {branding.configuration.sidebar.showEventGuide && (
                            <React.Fragment key={"EVENT_GUIDE"}>
                                <ButtonWrapper>
                                    <Button
                                        id="eg"
                                        caption={branding.configuration.sidebar.lblEventGuide}
                                        icon={EventGuideIcon}
                                        inverse
                                        onClick={visitEventGuide}
                                    />
                                </ButtonWrapper>
                            </React.Fragment>
                        )}
                    </Buttons>
                </Nav>
            </TopWrapper>
            <BottomWrapper className="bottom">
                <Languages />
            </BottomWrapper>
        </SidebarWrapper>
    )
}

export default Sidebar
